import React, { useEffect, useState } from "react";
import {
  Button,
  Typography,
  Modal,
  Input,
  message,
  Card,
  Row,
  Col,
  Space,
  Table,
  Popconfirm,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import axios from "axios";
import { useSelector } from "react-redux";
import { BASE_API_URL } from "../../../utils/consts";
import type { ColumnsType } from "antd/es/table";

const { Title, Text } = Typography;

interface InvestmentCategory {
  _id: string;
  clientId: string;
  name: string;
  description?: string;
  color: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const InvestmentCategoriesSettings: React.FC = () => {
  const userData = useSelector((state: any) => state.userData.user);
  const clientId = userData?.clientId;

  const [categories, setCategories] = useState<InvestmentCategory[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] =
    useState<InvestmentCategory | null>(null);
  const [loading, setLoading] = useState(false);

  // Form states
  const [categoryName, setCategoryName] = useState("");
  const [categoryDescription, setCategoryDescription] = useState("");
  const [categoryColor, setCategoryColor] = useState("#1890ff");

  useEffect(() => {
    if (clientId) {
      fetchCategories();
    }
  }, [clientId]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_API_URL}/investment-categories`,
        {
          params: { clientId },
        }
      );
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      message.error("Greška pri učitavanju kategorija");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (category?: InvestmentCategory) => {
    if (category) {
      setEditingCategory(category);
      setCategoryName(category.name);
      setCategoryDescription(category.description || "");
      setCategoryColor(category.color);
    } else {
      setEditingCategory(null);
      setCategoryName("");
      setCategoryDescription("");
      setCategoryColor("#1890ff");
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingCategory(null);
    setCategoryName("");
    setCategoryDescription("");
    setCategoryColor("#1890ff");
  };

  const handleSaveCategory = async () => {
    if (!categoryName.trim()) {
      message.error("Naziv kategorije je obavezan");
      return;
    }

    try {
      const payload = {
        clientId,
        name: categoryName.trim(),
        description: categoryDescription.trim(),
        color: categoryColor,
        createdBy: userData?.id,
        updatedBy: userData?.id,
      };

      if (editingCategory) {
        await axios.put(
          `${BASE_API_URL}/investment-categories/${editingCategory._id}`,
          payload
        );
        message.success("Kategorija je uspešno ažurirana");
      } else {
        await axios.post(`${BASE_API_URL}/investment-categories`, payload);
        message.success("Kategorija je uspešno kreirana");
      }

      handleCloseModal();
      fetchCategories();
    } catch (error: any) {
      console.error("Error saving category:", error);
      message.error(
        error.response?.data?.message || "Greška pri čuvanju kategorije"
      );
    }
  };

  const handleDeleteCategory = async (id: string) => {
    try {
      await axios.delete(`${BASE_API_URL}/investment-categories/${id}`);
      message.success("Kategorija je uspešno obrisana");
      fetchCategories();
    } catch (error) {
      console.error("Error deleting category:", error);
      message.error("Greška pri brisanju kategorije");
    }
  };

  const columns: ColumnsType<InvestmentCategory> = [
    {
      title: "Naziv",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <Space>
          <div
            style={{
              width: 16,
              height: 16,
              backgroundColor: record.color,
              borderRadius: "50%",
            }}
          />
          <Text strong>{name}</Text>
        </Space>
      ),
    },
    {
      title: "Opis",
      dataIndex: "description",
      key: "description",
      render: (description) => description || "-",
    },
    {
      title: "Boja",
      dataIndex: "color",
      key: "color",
      render: (color) => (
        <Space>
          <div
            style={{
              width: 24,
              height: 24,
              backgroundColor: color,
              border: "1px solid #d9d9d9",
              borderRadius: 4,
            }}
          />
          <Text type="secondary">{color}</Text>
        </Space>
      ),
    },
    {
      title: "Akcije",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          >
            Izmeni
          </Button>
          <Popconfirm
            title="Da li ste sigurni da želite da obrišete ovu kategoriju?"
            onConfirm={() => handleDeleteCategory(record._id)}
            okText="Da"
            cancelText="Ne"
          >
            <Button type="primary" danger icon={<DeleteOutlined />}>
              Obriši
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3}>Kategorije investicija</Title>
          <Text type="secondary">
            Upravljajte kategorijama za praćenje investicija
          </Text>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenModal()}
          >
            Dodaj kategoriju
          </Button>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={categories}
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingCategory ? "Izmeni kategoriju" : "Dodaj novu kategoriju"}
        open={isModalOpen}
        onOk={handleSaveCategory}
        onCancel={handleCloseModal}
        className="standardHeader standardFooter"
        okText="Sačuvaj"
        cancelText="Otkaži"
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <Text strong>Naziv kategorije *</Text>
            <Input
              placeholder="Npr. Oprema, Nekretnine, Vozila..."
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              style={{ marginTop: 8 }}
            />
          </div>

          <div>
            <Text strong>Opis</Text>
            <Input.TextArea
              placeholder="Opis kategorije (opciono)"
              value={categoryDescription}
              onChange={(e) => setCategoryDescription(e.target.value)}
              rows={3}
              style={{ marginTop: 8 }}
            />
          </div>

          <div>
            <Text strong>Boja</Text>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Input
                type="color"
                value={categoryColor}
                onChange={(e) => setCategoryColor(e.target.value)}
                style={{ width: 60, cursor: "pointer" }}
              />
              <Input
                value={categoryColor}
                onChange={(e) => setCategoryColor(e.target.value)}
                placeholder="#1890ff"
              />
            </div>
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default InvestmentCategoriesSettings;
